/**
 * Admin Authentication Service
 *
 * Handles admin bootstrap authentication using username/password.
 * This is separate from SSO authentication and provides fallback access
 * to configure the application before SSO is set up.
 */

const ADMIN_TOKEN_KEY = 'adminTokens';

interface AdminTokens {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_at: number;
  password_must_change?: boolean;
}

interface AuthConfig {
  sso_configured: boolean;
  sso_provider: string | null;
  cognito_domain: string;
  client_id: string;
  idp_name: string | null;
  admin_login_available: boolean;
}

interface AuthStatus {
  admin_setup_completed: boolean;
  sso_configured: boolean;
  active_provider: string | null;
}

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '/api';

export const adminAuthService = {
  /**
   * Store admin tokens in localStorage
   */
  setTokens(tokens: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_in: number;
    password_must_change?: boolean;
  }): void {
    const data: AdminTokens = {
      access_token: tokens.access_token,
      id_token: tokens.id_token,
      refresh_token: tokens.refresh_token,
      expires_at: Date.now() + tokens.expires_in * 1000,
      password_must_change: tokens.password_must_change,
    };
    localStorage.setItem(ADMIN_TOKEN_KEY, JSON.stringify(data));
  },

  /**
   * Get stored admin tokens
   */
  getTokens(): AdminTokens | null {
    const stored = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!stored) return null;
    try {
      const tokens = JSON.parse(stored) as AdminTokens;
      if (tokens.expires_at < Date.now()) {
        this.clearTokens();
        return null;
      }
      return tokens;
    } catch {
      return null;
    }
  },

  /**
   * Get the ID token for API calls
   */
  getIdToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.id_token || null;
  },

  /**
   * Check if admin is authenticated
   */
  isAuthenticated(): boolean {
    return this.getTokens() !== null;
  },

  /**
   * Check if admin needs to change password
   */
  mustChangePassword(): boolean {
    const tokens = this.getTokens();
    return tokens?.password_must_change || false;
  },

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  },

  /**
   * Logout admin
   */
  logout(): void {
    this.clearTokens();
    window.location.href = '/admin-setup';
  },

  /**
   * Login with username and password
   */
  async login(
    username: string,
    password: string
  ): Promise<{
    success: boolean;
    password_must_change?: boolean;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.detail || 'Login failed' };
      }

      const tokens = await response.json();
      this.setTokens(tokens);

      return {
        success: true,
        password_must_change: tokens.password_must_change,
      };
    } catch (error) {
      console.error('Admin login error:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Change admin password
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    const token = this.getIdToken();
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.detail || 'Password change failed' };
      }

      // Clear the password_must_change flag in stored tokens
      const tokens = this.getTokens();
      if (tokens) {
        tokens.password_must_change = false;
        localStorage.setItem(ADMIN_TOKEN_KEY, JSON.stringify(tokens));
      }

      return { success: true };
    } catch (error) {
      console.error('Password change error:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Get auth configuration (public endpoint)
   */
  async getAuthConfig(): Promise<AuthConfig> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/config`);
      if (!response.ok) {
        throw new Error('Failed to get auth config');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get auth config:', error);
      return {
        sso_configured: false,
        sso_provider: null,
        cognito_domain: '',
        client_id: '',
        idp_name: null,
        admin_login_available: true,
      };
    }
  },

  /**
   * Get auth status (public endpoint)
   */
  async getAuthStatus(): Promise<AuthStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/status`);
      if (!response.ok) {
        throw new Error('Failed to get auth status');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get auth status:', error);
      return {
        admin_setup_completed: false,
        sso_configured: false,
        active_provider: null,
      };
    }
  },
};
